import React, { useEffect, useRef, useState } from 'react';
import './PricingComponent.scss';
import ButtonComponent from '../ButtonComponent/ButtonComponent';
import emailjs from '@emailjs/browser';

import {
  db,
  collection,
  addDoc,
  setDoc,
  doc,
  serverTimestamp,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '../firebase/firebase';

const FULL_PACKAGE = {
  name: 'Full Package',
  price: '9,500 LKR',
  features: [
    'Digital Invitation',
    'Liquor counts',
    'Guest Counts',
    'Guest RSVPs',
    'RSVP Counts',
    'Seating Arrangements',
    'Budget Management',
    'QR Code',
    'Guests adding by Plan My Guest team',
  ],
};

const initialForm = {
  weddingDate: '',
  weddingLocation: '',
  poruwaTime: '',
  ceremonyTime: '',
  hotelAddress: '',
  brideParentSurname: '',
  groomParentSurname: '',
  guestCount: '',
  contactNumber: '',
};

const PricingComponent = () => {
  const sectionRef = useRef(null);
  const firstFieldRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);

  const [form, setForm] = useState(initialForm);
  const [paymentProof, setPaymentProof] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.25 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (isModalOpen && firstFieldRef.current) {
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (thanksOpen) setThanksOpen(false);
        else setIsModalOpen(false);
      }
    };

    if (isModalOpen || thanksOpen) {
      window.addEventListener('keydown', onKey);
    }

    return () => window.removeEventListener('keydown', onKey);
  }, [isModalOpen, thanksOpen]);

  const onOpenModal = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setErrors({});
  };

  const onBackdropClick = (e) => {
    if (e.target.getAttribute('data-backdrop') === 'true') {
      if (thanksOpen) setThanksOpen(false);
      else onCloseModal();
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((f) => ({ ...f, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const onFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPaymentProof(files);

    if (errors.paymentProof) {
      setErrors((prev) => ({ ...prev, paymentProof: undefined }));
    }
  };

  const validate = () => {
    const next = {};

    if (!form.weddingDate) next.weddingDate = 'Required';
    if (!form.weddingLocation) next.weddingLocation = 'Required';
    if (!form.poruwaTime) next.poruwaTime = 'Required';
    if (!form.ceremonyTime) next.ceremonyTime = 'Required';
    if (!form.hotelAddress) next.hotelAddress = 'Required';
    if (!form.brideParentSurname) next.brideParentSurname = 'Required';
    if (!form.groomParentSurname) next.groomParentSurname = 'Required';

    if (!form.guestCount) {
      next.guestCount = 'Required';
    } else if (Number.isNaN(Number(form.guestCount))) {
      next.guestCount = 'Enter a number';
    }

    if (!form.contactNumber) {
      next.contactNumber = 'Required';
    } else {
      const digits = String(form.contactNumber).replace(/\D+/g, '');
      if (digits.length < 7) next.contactNumber = 'Enter a valid contact number';
    }

    if (!paymentProof || paymentProof.length === 0) {
      next.paymentProof = 'Payment proof is required';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const clearAll = () => {
    setForm(initialForm);
    setPaymentProof([]);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);

    try {
      const booking = {
        plan: FULL_PACKAGE.name,
        packagePrice: 9500,
        packageCurrency: 'LKR',
        packageFeatures: FULL_PACKAGE.features,

        weddingDate: form.weddingDate,
        weddingLocation: form.weddingLocation,
        poruwaTime: form.poruwaTime,
        ceremonyTime: form.ceremonyTime,
        hotelAddress: form.hotelAddress,
        brideParentSurname: form.brideParentSurname,
        groomParentSurname: form.groomParentSurname,
        guestCount: Number(form.guestCount) || null,
        contactNumber: form.contactNumber,

        createdAt: serverTimestamp(),
        status: 'pending',
        source: 'website-pricing',
      };

      const docRef = await addDoc(collection(db, 'bookings'), booking);

      try {
        const urls = await Promise.all(
          (paymentProof || []).map(async (file) => {
            const safeName = file.name.replace(/[^\w.\-]+/g, '_');
            const path = `paymentProofs/${docRef.id}/${Date.now()}_${safeName}`;
            const fileRef = ref(storage, path);

            await uploadBytes(fileRef, file);
            return await getDownloadURL(fileRef);
          })
        );

        if (urls.length > 0) {
          await setDoc(
            doc(db, 'bookings', docRef.id),
            { paymentProofUrls: urls },
            { merge: true }
          );
        }
      } catch (uploadErr) {
        console.error('Upload failed:', uploadErr);
        alert('Your booking was saved, but file upload failed. Please contact us.');
      }

      try {
        const payload = {
          plan: FULL_PACKAGE.name,
          packagePrice: 9500,
          ...form,
          ts: Date.now(),
        };

        sessionStorage.setItem('pmg_checkout_form', JSON.stringify(payload));
      } catch {}

      try {
        const REACT_APP_TEMPLATE_ID = process.env.REACT_APP_TEMPLATE_ID_BOOKINGS;

        emailjs
          .send(
            process.env.REACT_APP_SERVICE,
            REACT_APP_TEMPLATE_ID,
            {
              plan: FULL_PACKAGE.name,
              packagePrice: '9,500 LKR',
              packageFeatures: FULL_PACKAGE.features.join(', '),
              weddingDate: form.weddingDate,
              weddingLocation: form.weddingLocation,
              poruwaTime: form.poruwaTime,
              ceremonyTime: form.ceremonyTime,
              hotelAddress: form.hotelAddress,
              brideParentSurname: form.brideParentSurname,
              groomParentSurname: form.groomParentSurname,
              guestCount: Number(form.guestCount) || null,
              contactNumber: form.contactNumber,
            },
            process.env.REACT_APP_PUBLIC_ID
          )
          .then(() => {
            console.log('Email sent successfully');
          })
          .catch((error) => {
            console.error('EmailJS error:', error?.text || error.message || JSON.stringify(error));
          });
      } catch (error) {
        console.error('EmailJS setup error:', error);
      }

      setIsModalOpen(false);
      clearAll();
      setThanksOpen(true);
    } catch (err) {
      console.error('Booking save failed:', err);
      alert('Sorry, we could not save your booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className={`pricing ${visible ? 'is-visible' : ''}`}
      id="pricing"
      aria-label="Packages & Pricing"
    >
      <header className="pricing__head">
        <p className="pricing__eyebrow">Packages & Pricing</p>
        <h2 className="pricing__title">Everything you need for your wedding day</h2>
        <p className="pricing__lead">
          Get the complete Plan My Guest package for one simple price.
        </p>
      </header>

      <div className="pricing__singleWrap">
        <article className="plan fullPackage" aria-label="Full package">
          <div className="plan__badge">Full Package</div>

          <header className="plan__header">
            <p className="plan__mini">Complete wedding management solution</p>
            <h3 className="plan__name">{FULL_PACKAGE.name}</h3>

            <div className="plan__priceBlock">
              <span className="plan__currency">LKR</span>
              <span className="plan__amount">9,500</span>
              <span className="plan__only">only</span>
            </div>

            <p className="plan__desc">
              For the full package, you can manage your wedding guests, RSVPs,
              seating, budget, QR check-in and more all in one place.
            </p>
          </header>

          <ul className="plan__features fullPackage__features">
            {FULL_PACKAGE.features.map((feature, index) => (
              <li className="plan__feature" key={index}>
                <svg
                  className="plan__check"
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    d="M4.5 10.5l3.5 3.5 7.5-7.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <div className="fullPackage__highlight">
            <span>Guest adding service included</span>
            <strong>Handled by Plan My Guest team</strong>
          </div>

          {/* <div className="plan__ctaWrap">
            <ButtonComponent
              variant="primary"
              className="plan__cta"
              onClick={onOpenModal}
            >
              Book Full Package
            </ButtonComponent>
          </div> */}
        </article>
      </div>

      {isModalOpen && (
        <div
          className="paymodal__backdrop"
          data-backdrop="true"
          onClick={onBackdropClick}
          role="presentation"
        >
          <div
            className="paymodal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="paymodal-title"
          >
            <header className="paymodal__header">
              <h3 id="paymodal-title" className="paymodal__title">
                Full Package — 9,500 LKR
              </h3>

              <ButtonComponent
                variant="ghost"
                className="paymodal__close"
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </ButtonComponent>
            </header>

            <form className="paymodal__form" onSubmit={handleSubmit} noValidate>
              <div className="frm">
                <div className="frm__col">
                  <label className="frm__label">
                    Wedding date
                    <input
                      ref={firstFieldRef}
                      type="date"
                      name="weddingDate"
                      className={`frm__input ${errors.weddingDate ? 'has-error' : ''}`}
                      value={form.weddingDate}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.weddingDate && <span className="frm__error">{errors.weddingDate}</span>}
                  </label>

                  <label className="frm__label">
                    Wedding location
                    <input
                      type="text"
                      name="weddingLocation"
                      placeholder="City / venue"
                      className={`frm__input ${errors.weddingLocation ? 'has-error' : ''}`}
                      value={form.weddingLocation}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.weddingLocation && <span className="frm__error">{errors.weddingLocation}</span>}
                  </label>

                  <label className="frm__label">
                    Poruwa time
                    <input
                      type="time"
                      name="poruwaTime"
                      className={`frm__input ${errors.poruwaTime ? 'has-error' : ''}`}
                      value={form.poruwaTime}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.poruwaTime && <span className="frm__error">{errors.poruwaTime}</span>}
                  </label>

                  <label className="frm__label">
                    Ceremony time
                    <input
                      type="time"
                      name="ceremonyTime"
                      className={`frm__input ${errors.ceremonyTime ? 'has-error' : ''}`}
                      value={form.ceremonyTime}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.ceremonyTime && <span className="frm__error">{errors.ceremonyTime}</span>}
                  </label>
                </div>

                <div className="frm__col">
                  <label className="frm__label">
                    Hotel address
                    <input
                      type="text"
                      name="hotelAddress"
                      placeholder="Hotel / reception address"
                      className={`frm__input ${errors.hotelAddress ? 'has-error' : ''}`}
                      value={form.hotelAddress}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.hotelAddress && <span className="frm__error">{errors.hotelAddress}</span>}
                  </label>

                  <label className="frm__label">
                    Bride's parent surname
                    <input
                      type="text"
                      name="brideParentSurname"
                      placeholder="e.g., Perera"
                      className={`frm__input ${errors.brideParentSurname ? 'has-error' : ''}`}
                      value={form.brideParentSurname}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.brideParentSurname && <span className="frm__error">{errors.brideParentSurname}</span>}
                  </label>

                  <label className="frm__label">
                    Groom's parent surname
                    <input
                      type="text"
                      name="groomParentSurname"
                      placeholder="e.g., Silva"
                      className={`frm__input ${errors.groomParentSurname ? 'has-error' : ''}`}
                      value={form.groomParentSurname}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.groomParentSurname && <span className="frm__error">{errors.groomParentSurname}</span>}
                  </label>

                  <label className="frm__label">
                    Guest count
                    <input
                      type="number"
                      name="guestCount"
                      min="1"
                      step="1"
                      placeholder="e.g., 250"
                      className={`frm__input ${errors.guestCount ? 'has-error' : ''}`}
                      value={form.guestCount}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.guestCount && <span className="frm__error">{errors.guestCount}</span>}
                  </label>

                  <label className="frm__label">
                    Contact number
                    <input
                      type="tel"
                      name="contactNumber"
                      placeholder="+94 70 123 4567"
                      className={`frm__input ${errors.contactNumber ? 'has-error' : ''}`}
                      value={form.contactNumber}
                      onChange={onChange}
                      disabled={submitting}
                    />
                    {errors.contactNumber && <span className="frm__error">{errors.contactNumber}</span>}
                  </label>
                </div>
              </div>

              <div className="frm__proof">
                <label className="frm__label">
                  Payment proof images/PDF
                  <div className={`dropzone ${errors.paymentProof ? 'has-error' : ''}`}>
                    <input
                      type="file"
                      name="paymentProof"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={onFilesChange}
                      className="dropzone__input"
                      aria-label="Upload payment proof files"
                      disabled={submitting}
                    />
                    <div className="dropzone__hint">
                      Drag & drop or click to upload PNG, JPG or PDF
                    </div>
                  </div>
                  {errors.paymentProof && <span className="frm__error">{errors.paymentProof}</span>}
                </label>

                {paymentProof?.length > 0 && (
                  <ul className="filelist">
                    {paymentProof.map((f, i) => (
                      <li key={i} className="filelist__item">
                        <span className="filelist__name">{f.name}</span>
                        <span className="filelist__meta">{(f.size / 1024).toFixed(0)} KB</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="paymodal__footer">
                <ButtonComponent
                  type="button"
                  variant="ghost"
                  className="paymodal__btn"
                  onClick={onCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </ButtonComponent>

                <ButtonComponent
                  type="submit"
                  variant="primary"
                  className="paymodal__btn"
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : 'Make the Booking'}
                </ButtonComponent>
              </div>
            </form>
          </div>
        </div>
      )}

      {thanksOpen && (
        <div
          className="paymodal__backdrop"
          data-backdrop="true"
          onClick={onBackdropClick}
          role="presentation"
        >
          <div
            className="paymodal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="thanks-title"
          >
            <header className="paymodal__header">
              <h3 id="thanks-title" className="paymodal__title">
                Thank you!
              </h3>

              <ButtonComponent
                variant="ghost"
                className="paymodal__close"
                aria-label="Close"
                onClick={() => setThanksOpen(false)}
              >
                ✕
              </ButtonComponent>
            </header>

            <div className="paymodal__body">
              <p>
                <strong>Thank you for making a booking with Plan My Guest.</strong>
                <br />
                We’ll get back to you shortly.
              </p>
            </div>

            <div className="paymodal__footer">
              <ButtonComponent
                type="button"
                variant="primary"
                className="paymodal__btn"
                onClick={() => setThanksOpen(false)}
              >
                Close
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingComponent;